import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { storePath } from "../lib/storeUrl";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, Trash2, ShoppingBag, ShieldCheck, Truck, ArrowRight } from "lucide-react";
import api, { fmtINR } from "../lib/api";
import { getCart, saveCart, cartCount, cartSubtotal } from "../lib/cart";
import { themeVars, headingFont, SafeImg } from "../components/StoreKit";
import { setFavicon } from "../lib/utils";

export default function CartPage({ slugOverride = null }) {
  const { slug: routeSlug } = useParams();
  const slug = slugOverride || routeSlug;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [cart, setCart] = useState(() => getCart(slug));

  useEffect(() => {
    api.get(`/public/store/${slug}`).then((r) => { setData(r.data); const fav = r.data.store?.settings?.favicon_url || r.data.branding?.favicon_url; if (fav) setFavicon(fav); }).catch(() => setError("Store unavailable"));
  }, [slug]);
  useEffect(() => saveCart(slug, cart), [cart, slug]);

  const store = data?.store;
  const theme = data?.theme;
  const colors = store?.settings?.colors || theme?.colors || {};
  const vars = themeVars(colors, theme);
  const head = headingFont(theme);
  const radius = theme?.radius || "10px";
  const shippingFee = Number(store?.settings?.shipping_fee || 0);
  const taxPct = Number(store?.settings?.tax_percent || 0);

  const count = cartCount(cart);
  const subtotal = cartSubtotal(cart);
  const shipping = subtotal >= 999 || subtotal === 0 ? 0 : shippingFee;
  const tax = Math.round(subtotal * taxPct / 100);
  const total = subtotal + shipping + tax;

  const setQty = (key, d) => setCart((prev) => prev.map((c) => (c.key === key ? { ...c, qty: Math.max(1, c.qty + d) } : c)));
  const removeItem = (key) => setCart((prev) => prev.filter((c) => c.key !== key));

  if (error) return <div className="min-h-screen flex items-center justify-center bg-background"><p data-testid="cart-error" className="text-muted-foreground">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-10 w-10 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>;

  return (
    <div style={{ ...vars, fontFamily: "var(--sbfont)" }} className="min-h-screen bg-[var(--sbg)] text-[var(--st)]" data-testid="cart-page">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <Link to={storePath(slug, "")} data-testid="cart-back-to-store" className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity">
            <ArrowLeft className="h-4 w-4" /> {store.name}
          </Link>
          <span className="ml-auto flex items-center gap-1.5 text-xs font-bold" style={{ color: "var(--st)", opacity: 0.5 }}>
            <ShoppingBag className="h-4 w-4" style={{ color: "var(--sa)" }} /> {count} item{count === 1 ? "" : "s"}
          </span>
        </div>
      </header>

      {cart.length === 0 ? (
        <div className="max-w-md mx-auto text-center py-28 px-6" data-testid="cart-empty">
          <div className="h-20 w-20 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: "var(--ss)" }}>
            <ShoppingBag className="h-8 w-8" style={{ color: "var(--sp)" }} />
          </div>
          <h1 className={`${head} text-2xl mt-6`} style={{ color: "var(--sp)" }}>Your cart is empty</h1>
          <p className="text-sm mt-2" style={{ color: "var(--st)", opacity: 0.55 }}>Add something you love — it will show up here.</p>
          <Link to={storePath(slug, "")} data-testid="cart-empty-shop"
            className="inline-block mt-7 px-8 py-3.5 rounded-full font-bold text-sm text-white transition-transform active:scale-95"
            style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
            Browse products
          </Link>
        </div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-10">
          {/* Items */}
          <div>
            <h1 className={`${head} text-3xl tracking-tight`} style={{ color: "var(--sp)" }}>
              Your Cart <span className="text-base font-sans font-bold" style={{ color: "var(--st)", opacity: 0.45 }}>{count} item{count === 1 ? "" : "s"}</span>
            </h1>
            <div className="space-y-4 mt-7">
              {cart.map((c, i) => (
                <motion.div key={c.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="bg-white border border-black/5 p-4 flex gap-4 items-center shadow-sm" style={{ borderRadius: radius }} data-testid={`cart-item-${c.key}`}>
                  <div className="h-20 w-20 rounded-lg bg-zinc-100 overflow-hidden shrink-0">
                    <SafeImg src={c.image} alt={c.name} className="h-full w-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate" style={{ color: "var(--sp)" }}>{c.name}</p>
                    {c.variation && <p className="text-[11px] mt-0.5" style={{ color: "var(--st)", opacity: 0.5 }}>{c.variation}</p>}
                    <p className="text-xs mt-1 font-semibold" style={{ color: "var(--st)", opacity: 0.6 }}>{fmtINR(c.price)} each</p>
                    <div className="flex items-center gap-1 mt-2.5">
                      <button data-testid={`qty-minus-${c.key}`} onClick={() => setQty(c.key, -1)}
                        className="h-8 w-8 rounded-full border-2 flex items-center justify-center transition-colors hover:bg-black/5" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span data-testid={`qty-value-${c.key}`} className="w-9 text-center text-sm font-extrabold">{c.qty}</span>
                      <button data-testid={`qty-plus-${c.key}`} onClick={() => setQty(c.key, 1)}
                        className="h-8 w-8 rounded-full border-2 flex items-center justify-center transition-colors hover:bg-black/5" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-display font-extrabold text-lg" style={{ color: "var(--sp)" }} data-testid={`line-total-${c.key}`}>{fmtINR(c.price * c.qty)}</p>
                    <button data-testid={`remove-item-${c.key}`} onClick={() => removeItem(c.key)}
                      className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-red-500 hover:text-red-600 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
            <Link to={storePath(slug, "")} data-testid="continue-shopping"
              className="inline-flex items-center gap-2 mt-7 text-sm font-bold hover:opacity-70 transition-opacity" style={{ color: "var(--sp)" }}>
              <ArrowLeft className="h-4 w-4" /> Continue shopping
            </Link>
          </div>

          {/* Summary */}
          <div className="lg:sticky lg:top-20 h-fit">
            <div className="bg-white rounded-xl border border-black/5 overflow-hidden shadow-sm" data-testid="cart-summary">
              <div className="px-6 py-4 border-b border-black/5" style={{ backgroundColor: "var(--ss)" }}>
                <p className="font-display font-extrabold" style={{ color: "var(--sp)" }}>Order Summary</p>
              </div>
              <div className="p-6 space-y-2.5 text-sm">
                <div className="flex justify-between"><span style={{ color: "var(--st)", opacity: 0.6 }}>Subtotal ({count} items)</span><span className="font-bold" data-testid="cart-subtotal">{fmtINR(subtotal)}</span></div>
                <div className="flex justify-between"><span style={{ color: "var(--st)", opacity: 0.6 }}>Shipping</span><span className="font-bold" data-testid="cart-shipping">{shipping ? fmtINR(shipping) : "Free"}</span></div>
                {tax > 0 && <div className="flex justify-between"><span style={{ color: "var(--st)", opacity: 0.6 }}>Tax ({taxPct}%)</span><span className="font-bold" data-testid="cart-tax">{fmtINR(tax)}</span></div>}
                {subtotal < 999 && shippingFee > 0 && (
                  <p className="text-[11px] font-bold flex items-center gap-1.5 pt-1" style={{ color: "var(--sa)" }}>
                    <Truck className="h-3.5 w-3.5" /> Add {fmtINR(999 - subtotal)} more for free shipping
                  </p>
                )}
                <div className="flex justify-between pt-3 border-t border-black/10">
                  <span className="font-extrabold">Total</span>
                  <span className="font-display font-extrabold text-2xl" style={{ color: "var(--sp)" }} data-testid="cart-total">{fmtINR(total)}</span>
                </div>
              </div>
              <div className="px-6 pb-6">
                <button data-testid="proceed-to-checkout" onClick={() => navigate(storePath(slug, "/checkout"))}
                  className="w-full h-13 py-4 rounded-full font-extrabold text-sm text-white transition-all active:scale-95 hover:opacity-90 shadow-lg"
                  style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
                  Proceed to Checkout · {fmtINR(total)} <ArrowRight className="inline h-4 w-4 ml-1" />
                </button>
                <p className="flex items-center justify-center gap-1.5 text-[11px] font-bold mt-3" style={{ color: "var(--st)", opacity: 0.5 }}>
                  <ShieldCheck className="h-3.5 w-3.5" style={{ color: "var(--sa)" }} /> Secure checkout · COD & online payment
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
